// Suivre un groupe ou une équipe (D-110) : les mots des écrans, logique pure (testée).
//  - Un groupe (« Seniors filles ») engage une ou plusieurs équipes (SF1, SF2) ; le suivre, c'est suivre
//    toutes ses équipes engagées, ou une seule.
//  - Trois réglages : le résultat et les changements cochés par défaut, la veille du match au choix.
//  - Les membres d'une équipe la suivent d'office (résultat, changements), sans veille.

export interface FollowSquad { id: string; name: string }
export interface FollowPref { squad_id: string | null; result: boolean; changes: boolean; eve: boolean; since?: string }
export interface FollowAuto { result: boolean; changes: boolean; self: boolean; who: string[] }
export interface FollowGroup {
  team_id: string; name: string; category: string | null; gender: string | null; squads: FollowSquad[];
  follow: FollowPref | null; auto: FollowAuto | null; followers: number;
}
export interface FollowState {
  club_id: string; abonne: boolean; origin: 'public' | 'app' | null; since: string | null; email_digest: boolean; minor: boolean;
  groups: FollowGroup[];
}

/** Les réglages d'un nouveau suivi : résultat et changements, pas la veille. */
export const DEFAULT_PREF: FollowPref = { squad_id: null, result: true, changes: true, eve: false };

const NUM = ['', 'Une', 'Deux', 'Trois', 'Quatre', 'Cinq', 'Six'];

/** « SF1 », « SF1 et SF2 », « SF1, SF2 et SF3 ». */
export function listFr(xs: string[]): string {
  if (xs.length < 2) return xs.join('');
  return `${xs.slice(0, -1).join(', ')} et ${xs[xs.length - 1]}`;
}

/** Le groupe engage plusieurs équipes (sinon il est une seule équipe). */
export const manySquads = (g: Pick<FollowGroup, 'squads'>) => g.squads.length > 1;

/** Ce que je suis dans ce groupe : l'équipe choisie, toutes ses équipes, ou le groupe lui-même. */
export function scopeText(g: Pick<FollowGroup, 'squads' | 'name'>, squadId: string | null): string {
  if (squadId) return g.squads.find(q => q.id === squadId)?.name ?? g.name;
  return manySquads(g) ? listFr(g.squads.map(q => q.name)) : g.name;
}

/** « résultats et changements », « résultats, changements et veille du match », « veille du match ». */
export function settingsWords(p: Pick<FollowPref, 'result' | 'changes'> & { eve?: boolean }): string {
  return listFr([p.result ? 'résultats' : '', p.changes ? 'changements' : '', p.eve ? 'veille du match' : ''].filter(Boolean));
}

/** Les réglages en abrégé (récapitulatif) : « résultat · changements ». */
export function settingsShort(p: Pick<FollowPref, 'result' | 'changes'> & { eve?: boolean }): string {
  return [p.result ? 'résultat' : '', p.changes ? 'changements' : '', p.eve ? 'veille du match' : ''].filter(Boolean).join(' · ');
}

/** Suivi en cours dans ce groupe (le mien ou d'office, s'il n'est pas coupé). */
export function isFollowing(g: Pick<FollowGroup, 'follow' | 'auto'>): boolean {
  return !!g.follow || !!(g.auto && (g.auto.result || g.auto.changes));
}

/** La bande du groupe sur la page publique, sous les pastilles. */
export function stripText(g: FollowGroup): string {
  if (g.auto && (g.auto.result || g.auto.changes)) return `Tu les suis d’office : ${settingsWords(g.auto)}`;
  if (g.follow) {
    const words = settingsWords(g.follow);
    return manySquads(g) || g.follow.squad_id ? `Tu suis ${scopeText(g, g.follow.squad_id)} : ${words}` : `Tu suis ses ${words}`;
  }
  if (manySquads(g)) return `${NUM[g.squads.length] ?? g.squads.length} équipes engagées, ${listFr(g.squads.map(q => q.name))} : leurs résultats sur ton téléphone`;
  return 'Ses résultats et ses changements sur ton téléphone';
}

/** « Kenza y joue », « Tu y joues », « Kenza et Lucas y jouent ». */
export function autoWho(a: FollowAuto): string {
  const names = [...(a.self ? ['Tu'] : []), ...a.who];
  if (!names.length) return 'Ton équipe';
  if (names.length === 1) return names[0] === 'Tu' ? 'Tu y joues' : `${names[0]} y joue`;
  return `${listFr(names)} y jouent`;
}

/** « depuis le 29 oct. » */
export function sinceText(iso: string | null | undefined): string {
  if (!iso) return '';
  return `depuis le ${new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
}

/** « Gymnase de Rivebelle » → « au Gymnase de Rivebelle » ; « Salle des sports » → « à la salle des sports » ;
 *  sinon « · Lieu ». */
export function atPlace(place: string | null | undefined): string {
  const p = (place ?? '').trim();
  if (!p) return '';
  if (/^(gymnase|complexe|palais|stade|centre|dojo|boulodrome)\b/i.test(p)) return ` au ${p}`;
  if (/^(salle|halle)\b/i.test(p)) return ` à la ${p.charAt(0).toLowerCase()}${p.slice(1)}`;
  return ` · ${p}`;
}

export interface NextMatch { starts_at: string; versus: string; is_home: boolean | null; location?: string | null }

/** Après « Suivre » : « SF2 à Lusmagne, samedi 31 octobre à 13h00, puis SF1 – ASC Valdorne à 20h30 au Gymnase de
 *  Rivebelle » (les matchs du premier jour, deux au plus) ; '' sans match à venir. */
export function nextMatchesParts(list: NextMatch[], tz: string): { versus: string; tail: string }[] {
  const sorted = [...list].sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const first = sorted[0];
  if (!first) return [];
  const f = (iso: string, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('fr-FR', { timeZone: tz, ...o }).format(new Date(iso));
  const day = (iso: string) => f(iso, { weekday: 'long', day: 'numeric', month: 'long' });
  const hour = (iso: string) => f(iso, { hour: '2-digit', minute: '2-digit' }).replace(':', 'h');
  const sameDay = sorted.filter(m => day(m.starts_at) === day(first.starts_at)).slice(0, 2);
  const where = (m: NextMatch) => (m.is_home ? atPlace(m.location) : '');
  return sameDay.map((m, i) => ({
    versus: m.versus,
    tail: i === 0 ? `, ${day(m.starts_at)} à ${hour(m.starts_at)}${where(m)}` : ` à ${hour(m.starts_at)}${where(m)}`,
  }));
}

/** La journée regroupée dans la cloche : chaque score, ceux gagnés (✓) en gras. */
export function dayScores(body: string): { text: string; win: boolean }[] {
  return body.split(' · ').map(t => ({ text: t, win: /✓\s*$/.test(t) }));
}
